import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "budgetColumn", "validation", "status"]
  static values = { url: String }

  connect() {
    this.revision = (this.revision || 0) + 1
    this.updateDisplay()
  }

  disconnect() {
    this.revision += 1
    window.clearTimeout(this.saveTimer)
  }

  calculate() {
    const revision = ++this.revision
    const valid = this.updateDisplay()
    window.clearTimeout(this.saveTimer)
    if (!valid) return

    if (!this.hasUrlValue) {
      if (this.inputTarget.value.trim() !== "") this.statusTarget.textContent = "Budget preview only. This amount is not saved."
      return
    }

    this.statusTarget.textContent = "Saving…"
    this.saveTimer = window.setTimeout(() => this.persist(revision), 500)
  }

  updateDisplay() {
    const raw = this.inputTarget.value.trim()
    if (raw === "") {
      this.hideValidation()
      this.updateColumns(null)
      this.statusTarget.textContent = "Enter a planned budget to see suggested dollar amounts."
      return true
    }

    const normalized = this.normalizedBudget()
    const budget = Number(normalized)
    const budgetCents = Math.round(budget * 100)
    if (!Number.isSafeInteger(budgetCents) || budgetCents <= 0) {
      this.showValidation("Enter a planned advertising budget greater than $0.")
      this.updateColumns(null)
      this.statusTarget.textContent = ""
      return false
    }

    this.hideValidation()
    this.updateColumns(budget)
    return true
  }

  updateColumns(budget) {
    if (budget === null) {
      this.budgetColumnTargets.forEach(element => {
        element.textContent = element.dataset.pendingLabel || ""
        element.classList.toggle("hidden", !element.dataset.pendingLabel)
        element.classList.toggle("strategy-channel-budget__amount--pending", !!element.dataset.pendingLabel)
      })
      return
    }

    const budgetCents = Math.round(budget * 100)
    const percentages = this.budgetColumnTargets.map(element => Number(element.dataset.spendPercentage))
    const totalPercentage = percentages.reduce((sum, percentage) => sum + percentage, 0)
    const allocations = this.allocateCents(budgetCents, percentages, totalPercentage)

    this.budgetColumnTargets.forEach((element, index) => {
      element.classList.remove("hidden", "strategy-channel-budget__amount--pending")
      element.textContent = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(allocations[index] / 100)
    })
  }

  allocateCents(totalCents, percentages, totalPercentage) {
    if (percentages.length === 0 || totalPercentage <= 0) return percentages.map(() => 0)

    const allocations = percentages.map(percentage => Math.floor(totalCents * percentage / totalPercentage))
    const remainders = percentages.map(percentage => totalCents * percentage % totalPercentage)
    let remainingCents = totalCents - allocations.reduce((sum, allocation) => sum + allocation, 0)

    remainders
      .map((remainder, index) => ({ remainder, index }))
      .sort((left, right) => right.remainder - left.remainder || left.index - right.index)
      .forEach(({ index }) => {
        if (remainingCents <= 0) return

        allocations[index] += 1
        remainingCents -= 1
      })

    return allocations
  }

  normalizedBudget() {
    return this.inputTarget.value.replace(/[$,\s]/g, "")
  }

  async persist(revision) {
    const fallbackMessage = "We couldn't save this budget. Try again."

    try {
      const response = await fetch(this.urlValue, {
        method: "PATCH",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({ campaign_budget: this.normalizedBudget() })
      })
      const body = await this.readJsonBody(response)
      if (revision !== this.revision) return
      if (!response.ok) {
        this.updateColumns(null)
        this.showValidation(body.error || fallbackMessage)
        this.statusTarget.textContent = ""
        return
      }

      this.statusTarget.textContent = this.inputTarget.value.trim() === "" ?
        "Enter a planned budget to see suggested dollar amounts." : "Budget saved."
    } catch {
      if (revision !== this.revision) return
      this.updateColumns(null)
      this.showValidation(fallbackMessage)
      this.statusTarget.textContent = ""
    }
  }

  async readJsonBody(response) {
    if (!response.headers.get("content-type")?.includes("application/json")) return {}

    try {
      return await response.json()
    } catch {
      return {}
    }
  }

  showValidation(message) {
    this.validationTarget.textContent = message
    this.validationTarget.classList.remove("hidden")
    this.inputTarget.setAttribute("aria-invalid", "true")
  }

  hideValidation() {
    this.validationTarget.textContent = ""
    this.validationTarget.classList.add("hidden")
    this.inputTarget.removeAttribute("aria-invalid")
  }
}
